import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getApp, getApps, initializeApp } from "firebase/app";
import { getDatabase, onValue, ref } from "firebase/database";
import RideTrackingMap from "../components/tracking/RideTrackingMap";
import "../components/tracking/ride-tracking.css";

const apiBase = () => (process.env.REACT_APP_APP_DOMAIN || "").replace(/\/$/, "");

const firebaseTrailToArray = (trailNode) => {
  if (!trailNode) return [];
  if (Array.isArray(trailNode)) {
    return trailNode.filter(Boolean);
  }
  return Object.values(trailNode).filter(Boolean);
};

export default function RideTracking() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const pollRef = useRef(null);
  const firebaseUnsubRef = useRef(null);
  const firebaseSubscribedRef = useRef(false);

  const applySnapshot = useCallback((payload) => {
    setData((prev) => {
      const trailFromFirebase = payload.locationTrail
        ? firebaseTrailToArray(payload.locationTrail)
        : null;

      return {
        ...(prev || {}),
        ...payload,
        pickup_location:
          payload.pickupLocation || payload.pickup_location || prev?.pickup_location,
        drop_location: payload.dropLocation || payload.drop_location || prev?.drop_location,
        driver_live_location:
          payload.driverLiveLocation ||
          payload.driver_live_location ||
          prev?.driver_live_location,
        location_trail:
          trailFromFirebase && trailFromFirebase.length > 0
            ? trailFromFirebase
            : payload.location_trail || prev?.location_trail || [],
        ride_status: payload.rideStatus ?? payload.ride_status ?? prev?.ride_status,
        ride_status_name:
          payload.rideStatusName || payload.ride_status_name || prev?.ride_status_name,
        tracking_active:
          payload.isLive ?? payload.tracking_active ?? prev?.tracking_active ?? false,
        is_completed: payload.is_completed ?? prev?.is_completed ?? false,
      };
    });
  }, []);

  const fetchTracking = useCallback(async () => {
    const response = await axios.get(
      `${apiBase()}/api/ride/track/${encodeURIComponent(token)}`,
    );
    if (!response.data?.status) {
      throw new Error(response.data?.message || "Invalid tracking link.");
    }
    setData(response.data.data);
    return response.data.data;
  }, [token]);

  const subscribeFirebase = useCallback(async () => {
    if (firebaseSubscribedRef.current) return;

    try {
      const configRes = await axios.get(
        `${apiBase()}/api/ride/track/${encodeURIComponent(token)}/config`,
      );
      if (!configRes.data?.status) return;

      const firebaseConfig = configRes.data.data;
      if (!firebaseConfig?.databaseURL) return;

      const appName = `ride-track-${token}`;
      const app = getApps().some((a) => a.name === appName)
        ? getApp(appName)
        : initializeApp(firebaseConfig, appName);
      const db = getDatabase(app);
      const trackingRef = ref(db, `public_tracking/${token}`);

      if (firebaseUnsubRef.current) {
        firebaseUnsubRef.current();
      }

      firebaseUnsubRef.current = onValue(trackingRef, (snapshot) => {
        const val = snapshot.val();
        if (!val) return;
        applySnapshot({
          pickupLocation: val.pickupLocation,
          dropLocation: val.dropLocation,
          driverLiveLocation: val.driverLiveLocation,
          locationTrail: val.locationTrail,
          rideStatus: val.rideStatus,
          rideStatusName: val.rideStatusName,
          isLive: val.isLive,
          is_completed: val.rideStatus === 6,
        });
      });
      firebaseSubscribedRef.current = true;
    } catch (err) {
      console.warn("Firebase live tracking unavailable, using polling.", err);
    }
  }, [token, applySnapshot]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        await fetchTracking();
      } catch (err) {
        if (mounted) {
          setError(err?.message || "Unable to load tracking.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    pollRef.current = setInterval(async () => {
      try {
        const trackingData = await fetchTracking();
        if (trackingData?.tracking_active) {
          await subscribeFirebase();
        }
        if (trackingData?.is_completed && pollRef.current) {
          clearInterval(pollRef.current);
        }
      } catch {
        /* keep polling on transient errors */
      }
    }, 10000);

    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
      if (firebaseUnsubRef.current) firebaseUnsubRef.current();
    };
  }, [fetchTracking, subscribeFirebase]);

  useEffect(() => {
    if (data?.tracking_active) {
      subscribeFirebase();
    }
  }, [data?.tracking_active, subscribeFirebase]);

  const mapsReady = typeof window !== "undefined" && window.google?.maps;
  const trackingActive = Boolean(data?.tracking_active);
  const isCompleted = Boolean(data?.is_completed);
  const waitingForStart = data && !trackingActive && !isCompleted;
  const showMap = mapsReady && (trackingActive || isCompleted || waitingForStart);

  if (loading) {
    return (
      <div className="ride-tracking-page">
        <div className="ride-tracking-card">
          <p>Loading live tracking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ride-tracking-page">
        <div className="ride-tracking-card ride-tracking-card--error">
          <h1>Tracking unavailable</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ride-tracking-page">
      <header className="ride-tracking-header">
        <img
          src="/assets/img/all-images/logo-orbi-black-transparent.png"
          alt="Orbicabs"
          className="ride-tracking-logo"
        />
        <div>
          <h1>Live ride tracking</h1>
          {data?.booking_label && <p className="ride-tracking-booking">{data.booking_label}</p>}
          <p className={`ride-tracking-status ${trackingActive ? "is-live" : ""}`}>
            {data?.ride_status_name || "Updating..."}
          </p>
        </div>
      </header>

      {waitingForStart && (
        <div className="ride-tracking-banner ride-tracking-banner--wait">
          Tracking will be available once the ride starts.
        </div>
      )}

      {isCompleted && (
        <div className="ride-tracking-banner ride-tracking-banner--done">
          Ride completed. Showing final route.
        </div>
      )}

      {trackingActive && (
        <div className="ride-tracking-banner ride-tracking-banner--live">
          <span className="ride-tracking-live-dot" />
          Live
        </div>
      )}

      <div className="ride-tracking-map-panel">
        {!showMap ? (
          <p className="ride-tracking-map-fallback">Map is loading...</p>
        ) : (
          <RideTrackingMap
            pickup={data?.pickup_location}
            drop={data?.drop_location}
            driverLocation={data?.driver_live_location}
            locationTrail={data?.location_trail || []}
            showDriver={trackingActive}
          />
        )}
      </div>

      <div className="ride-tracking-locations">
        <div className="ride-tracking-location-card ride-tracking-location-card--pickup">
          <span className="ride-tracking-location-badge" aria-hidden="true">
            P
          </span>
          <div className="ride-tracking-location-body">
            <strong>Pickup</strong>
            <p>{data?.pickup_location?.address || "—"}</p>
          </div>
        </div>
        <div className="ride-tracking-location-card ride-tracking-location-card--drop">
          <span className="ride-tracking-location-badge" aria-hidden="true">
            D
          </span>
          <div className="ride-tracking-location-body">
            <strong>Drop</strong>
            <p>{data?.drop_location?.address || "—"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
